import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import clubsService from "../features/clubs/clubsService";

/**
 * Admin maintenance for the clubs members pick as their home club.
 *
 * Soft-inactive only: there is deliberately no delete. Retiring a club
 * deactivates it instead, so no member silently drops back to an incomplete
 * profile. Local state like Organisations.jsx - nothing else reads this data.
 */
export default function Clubs() {
  const [clubs, setClubs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [editing, setEditing] = useState(null); // "new" | club | null
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await clubsService.listClubs();
      setClubs(response.data.clubs || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load clubs");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing("new");
    setName("");
    setFormError("");
  };

  const openRename = (club) => {
    setEditing(club);
    setName(club.name);
    setFormError("");
  };

  const closeDialog = () => {
    setEditing(null);
    setFormError("");
  };

  const save = async () => {
    setIsSaving(true);
    setFormError("");

    try {
      if (editing === "new") {
        await clubsService.createClub(name);
        setNotice(`Added ${name.trim()}`);
      } else {
        await clubsService.updateClub(editing.id, { name });
        setNotice(`Renamed to ${name.trim()}`);
      }

      closeDialog();
      await load();
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (club) => {
    try {
      await clubsService.updateClub(club.id, { is_active: !club.is_active });
      setNotice(
        club.is_active
          ? `${club.name} deactivated - existing members keep it, new members cannot pick it`
          : `${club.name} reactivated`,
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to update");
    }
  };

  const syncFromBsa = async () => {
    setIsSyncing(true);
    setError("");

    try {
      const response = await clubsService.syncClubsFromBsa();
      const { fetched, created, updated, failed } = response.data;
      setNotice(
        `Synced from BSA: ${fetched} clubs fetched, ${created} added, ${updated} updated` +
          (failed ? `, ${failed} skipped` : ""),
      );
      await load();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to sync from BSA",
      );
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">Clubs</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={syncFromBsa}
            disabled={isSyncing}
          >
            {isSyncing ? "Syncing..." : "Sync from BSA"}
          </Button>
          <Button variant="contained" onClick={openCreate}>
            Add club
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sync from BSA pulls the club list from Bowls South Africa - existing
        clubs are updated by name, and any you have deactivated stay
        deactivated. Members pick their home club from the active list when
        completing their profile.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {clubs.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
          No clubs yet. Add the clubs your members belong to.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Club</TableCell>
                <TableCell>District</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clubs.map((club) => (
                <TableRow key={club.id} hover>
                  <TableCell>{club.name}</TableCell>
                  <TableCell>{club.district_name || club.district_id || "-"}</TableCell>
                  <TableCell>
                    {club.is_active ? (
                      <Chip size="small" color="success" label="Active" />
                    ) : (
                      <Chip size="small" label="Inactive" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <Button size="small" onClick={() => openRename(club)}>
                        Rename
                      </Button>
                      <Switch
                        size="small"
                        checked={Boolean(club.is_active)}
                        onChange={() => toggleActive(club)}
                        inputProps={{ "aria-label": `Toggle ${club.name}` }}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={editing !== null} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editing === "new" ? "Add club" : `Rename ${editing?.name || ""}`}
        </DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Club name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            required
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={isSaving || !name.trim()}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
